import { pageboxAuthPath, returnToPageboxLogin } from "../pageboxNavigation";
import { PageboxAccountNotice } from "../PageboxAccountNotice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { usePageScale } from "@/ui/usePageScale";

const BASE = {
    W: 1264,
    MARGIN_X: 108,
    GAP_HDR_TO_FIRST: 32,

    HDR_H: 96,
    HDR_FZ: 40,
    BACK_SIZE: 56,

    TAB_H: 96,
    TAB_FZ: 34,
    TAB_GAP_TOP: 10,

    INPUT_W: 1048,
    INPUT_H: 124,
    INPUT_FZ: 35,

    BTN_W: 724,
    BTN_H: 124,
    BTN_FZ: 40,

    GAP_INPUT_TO_BTN: 64,
    GAP_AFTER_TABS: 56,

    HINT_FZ: 20,
    HINT_RESERVED_H: 44,

    CARD_W: 1122,
    CARD_H: 361,
    CARD_RADIUS: 16,
    CARD_FZ: 34,
    CARD_GAP_TOP: 56,

    BTN_ROW_GAP: 24,
} as const;

function useScale() {
    const { s, px } = usePageScale({ baseW: BASE.W, max: 1.2 });
    return { s, spx: px };
}

type TabKey = "username" | "password";

function onlyDigits(v: string) {
    return String(v || "").replace(/\D+/g, "");
}

function fmtYmd(d: string | Date) {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return "-";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}.${pad(dt.getMonth() + 1)}.${pad(dt.getDate())}`;
}

export default function FindAccountPage() {
    const { s, spx } = useScale();
    const nav = useNavigate();
    const loc = useLocation() as any;
    const [searchParams] = useSearchParams();
    const isPagebox = searchParams.get("app_id") === "pagebox";
    const qrEntry = isPagebox && searchParams.get("entry") === "qr";
    const returnUrl = searchParams.get("return_url") || String(loc.state?.returnUrl || "");

    const [tab, setTab] = useState<TabKey>(() => (loc.state?.tab === "password" ? "password" : "username"));

    // 아이디 찾기
    const [phone, setPhone] = useState("");
    const [idResult, setIdResult] = useState<{ username: string; username_masked: string; joined_at: string } | null>(null);
    const [idHint, setIdHint] = useState<{ type: "ok" | "err"; text: string } | null>(null);

    // 비밀번호 찾기
    const [username, setUsername] = useState("");
    const [pwHint, setPwHint] = useState<{ type: "ok" | "err"; text: string } | null>(null);
    const [busy, setBusy] = useState(false);

    const pwSessionIdRef = useRef<string | null>(null);
    const confirmInFlightRef = useRef(false);
    const confirmedRef = useRef(false);

    const phoneOk = useMemo(() => {
        const d = onlyDigits(phone);
        return d.length >= 10 && d.length <= 11;
    }, [phone]);

    const usernameOk = useMemo(() => /^[A-Za-z0-9]{1,10}$/.test(username.trim()), [username]);

    useEffect(() => {
        setIdHint(null);
        setPwHint(null);
    }, [tab]);

    useEffect(() => {
        const onMessage = (ev: MessageEvent) => {
            const d: any = ev.data;
            if (!d || d.type !== "KMC_DONE") return;
            const sid = String(d.session_id || "");
            if (sid && pwSessionIdRef.current && sid !== pwSessionIdRef.current) return;
            void confirmPasswordVerification();
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderHint = (hint: { type: "ok" | "err"; text: string } | null) => (
        <div
            style={{
                minHeight: spx(BASE.HINT_RESERVED_H),
                marginTop: spx(12),
                fontSize: spx(BASE.HINT_FZ),
                color: hint?.type === "err" ? "#d00" : hint?.type === "ok" ? "#0a7" : "transparent",
            }}
        >
            {hint?.text || ""}
        </div>
    );

    const Label = ({ children }: { children: React.ReactNode }) => (
        <div style={{ fontSize: spx(36), fontWeight: 900, color: "#111", marginBottom: spx(12) }}>{children}</div>
    );

    const TabBtn = ({ k, title }: { k: TabKey; title: string }) => {
        const active = tab === k;
        return (
            <button
                type="button"
                onClick={() => setTab(k)}
                style={{
                    flex: 1,
                    height: spx(BASE.TAB_H),
                    background: "transparent",
                    border: "none",
                    fontSize: spx(BASE.TAB_FZ),
                    fontWeight: 900,
                    color: "#111",
                    borderBottom: active ? "3px solid #111" : "1px solid #C8C8C8",
                    cursor: "pointer",
                }}
            >
                {title}
            </button>
        );
    };

    const findUsername = async () => {
        setIdHint(null);
        setIdResult(null);

        if (!phoneOk) {
            setIdHint({ type: "err", text: "휴대폰 번호를 확인해주세요." });
            return;
        }

        setBusy(true);
        try {
            const r = await fetch("/api/find/username", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ phone: onlyDigits(phone) }),
            });
            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                setIdHint({ type: "err", text: "휴대폰 번호 정보와 일치하는 아이디를 확인할 수 없습니다." });
                return;
            }
            setIdResult({ username: data.username, username_masked: data.username_masked, joined_at: data.joined_at });
        } catch {
            setIdHint({ type: "err", text: "아이디 찾기에 실패했습니다. 다시 시도해주세요." });
        } finally {
            setBusy(false);
        }
    };

    const startPasswordVerification = async () => {
        setPwHint(null);

        if (!usernameOk) {
            setPwHint({ type: "err", text: "아이디 형식을 확인해주세요." });
            return;
        }

        const popup = window.open("", "KMCISWindow", "width=420,height=720,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes");
        if (!popup) {
            setPwHint({ type: "err", text: "팝업이 차단되었습니다. 브라우저에서 팝업 허용 후 다시 시도해주세요." });
            return;
        }
        popup.document.write("<p style='font-family:sans-serif;padding:16px'>본인인증 준비 중...</p>");

        setBusy(true);
        try {
            confirmedRef.current = false;
            pwSessionIdRef.current = null;

            const r = await fetch("/api/password/start", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ username: username.trim() }),
            });
            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                setPwHint({ type: "err", text: "인증 시작에 실패했습니다. 아이디를 확인해주세요." });
                try { popup.close(); } catch {}
                return;
            }

            const sessionId: string = data.session_id;
            const kmc = data.kmc || {};
            pwSessionIdRef.current = sessionId;

            const form = popup.document.createElement("form");
            form.method = "POST";
            form.action = kmc.action;
            form.target = "KMCISWindow";
            form.referrerPolicy = "no-referrer";

            const add = (k: string, v: string) => {
                const input = popup.document.createElement("input");
                input.type = "hidden";
                input.name = k;
                input.value = v ?? "";
                form.appendChild(input);
            };

            add("tr_cert", kmc.tr_cert || "");
            add("tr_url", kmc.tr_url || "");
            add("tr_ver", kmc.tr_ver || "V2");

            popup.document.body.appendChild(form);
            form.referrerPolicy = "no-referrer";
            form.submit();

            setPwHint({ type: "ok", text: "휴대폰 인증을 완료해주세요." });
        } catch {
            setPwHint({ type: "err", text: "인증 시작에 실패했습니다. 다시 시도해주세요." });
        } finally {
            setBusy(false);
        }
    };

    const confirmPasswordVerification = async () => {
        const sid = pwSessionIdRef.current;
        if (!sid) return;

        if (confirmedRef.current) return;
        if (confirmInFlightRef.current) return;
        confirmInFlightRef.current = true;

        setBusy(true);
        try {
            const r = await fetch("/api/password/confirm", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ session_id: sid }),
            });
            const data = (await r.json().catch(() => ({}))) as any;

            if (!r.ok) {
                const code = String(data?.error || "");
                if (code === "SESSION_EXPIRED") {
                    setPwHint({ type: "err", text: "인증 시간이 만료되었습니다. 다시 시도해주세요." });
                    return;
                }
                if (code === "MISMATCH") {
                    setPwHint({ type: "err", text: "아이디와 휴대폰 번호가 일치하지 않습니다." });
                    return;
                }
                setPwHint({ type: "err", text: "인증 확인에 실패했습니다. 다시 시도해주세요." });
                return;
            }

            confirmedRef.current = true;
            const resetToken = String(data.reset_token || "");
            const confirmedUsername = String(data.username || username.trim() || "");
            if (!resetToken) {
                setPwHint({ type: "err", text: "인증 확인에 실패했습니다. 다시 시도해주세요." });
                return;
            }

            sessionStorage.setItem("bt.pwreset.token", resetToken);
            if (confirmedUsername) sessionStorage.setItem("bt.pwreset.username", confirmedUsername);
            if (returnUrl) sessionStorage.setItem("bt.pwreset.return_url", returnUrl);
            nav(isPagebox ? pageboxAuthPath("/auth/password-reset", qrEntry) : "/auth/password-reset", {
                replace: true, state: { reset_token: resetToken, returnUrl },
            });
        } finally {
            confirmInFlightRef.current = false;
            setBusy(false);
        }
    };

    return (
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "start center", width: "100%" }}>
            <div style={{ width: spx(BASE.W), background: "#fff" }}>
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        height: spx(BASE.HDR_H),
                        paddingLeft: spx(BASE.MARGIN_X),
                        paddingRight: spx(BASE.MARGIN_X),
                        borderBottom: "1px solid #E0E0E0",
                        position: "relative",
                    }}
                >
                    {!isPagebox && <button
                        type="button"
                        onClick={() => nav(-1)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: spx(BASE.BACK_SIZE),
                            height: spx(BASE.BACK_SIZE),
                        }}
                        aria-label="뒤로가기"
                    >
                        <svg width={spx(32)} height={spx(32)} viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>}
                    <div
                        style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: spx(BASE.HDR_FZ),
                            fontWeight: 900,
                            color: "#111",
                        }}
                    >
                        아이디/비밀번호 찾기
                    </div>
                </div>

                <div style={{ paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}>
                    <div style={{ height: spx(BASE.GAP_HDR_TO_FIRST) }} />

                    {isPagebox && <PageboxAccountNotice fontSize={spx(26)} password={tab === "password"} />}

                    <div style={{ display: "flex", gap: 0, marginTop: spx(BASE.TAB_GAP_TOP) }}>
                        <TabBtn k="username" title="아이디 찾기" />
                        <TabBtn k="password" title="비밀번호 찾기" />
                    </div>

                    <div style={{ height: spx(BASE.GAP_AFTER_TABS) }} />

                    {tab === "username" ? (
                        idResult ? (
                            <>
                                <div style={{ textAlign: "center", fontSize: spx(28), fontWeight: 800, color: "#111" }}>
                                    휴대폰 번호 정보와 일치하는 아이디를 확인하세요.
                                </div>

                                <div style={{ height: spx(BASE.CARD_GAP_TOP) }} />

                                <div style={{ display: "grid", placeItems: "center" }}>
                                    <div
                                        style={{
                                            width: spx(BASE.CARD_W),
                                            height: spx(BASE.CARD_H),
                                            border: "1.5px solid #000",
                                            borderRadius: s(BASE.CARD_RADIUS),
                                            display: "grid",
                                            placeItems: "center",
                                            textAlign: "center",
                                            padding: spx(24),
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <div style={{ fontSize: spx(BASE.CARD_FZ), fontWeight: 900, lineHeight: 1.7 }}>
                                            <div>아이디 : {idResult.username_masked}</div>
                                            <div>가입일 : {fmtYmd(idResult.joined_at)}</div>
                                            {qrEntry && <p>페이지박스 기기에서 찾은 아이디로 로그인해주세요.</p>}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ height: spx(BASE.CARD_GAP_TOP) }} />

                                <div style={{ display: "flex", justifyContent: "center", gap: spx(BASE.BTN_ROW_GAP) }}>
                                    {!qrEntry && <button
                                        type="button"
                                        onClick={() => {
                                            if (isPagebox) void returnToPageboxLogin();
                                            else if (returnUrl) window.location.href = returnUrl;
                                            else setIdResult(null);
                                        }}
                                        style={{
                                            width: spx(549),
                                            height: spx(BASE.BTN_H),
                                            borderRadius: s(12),
                                            background: "#000",
                                            color: "#fff",
                                            fontSize: spx(BASE.BTN_FZ),
                                            fontWeight: 900,
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {isPagebox ? "로그인으로 돌아가기" : "확인"}
                                    </button>}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTab("password");
                                            setUsername(idResult.username);
                                            setPwHint(null);
                                            setIdHint(null);
                                            setIdResult(null);
                                            setPhone("");
                                        }}
                                        style={{
                                            width: spx(549),
                                            height: spx(BASE.BTN_H),
                                            borderRadius: s(12),
                                            background: "#C8C8C8",
                                            color: "#000",
                                            fontSize: spx(BASE.BTN_FZ),
                                            fontWeight: 900,
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        비밀번호 재설정
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <Label>휴대폰 번호 입력</Label>
                                <input
                                    placeholder="휴대폰 번호 입력"
                                    inputMode="numeric"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    style={{
                                        width: spx(BASE.INPUT_W),
                                        height: spx(BASE.INPUT_H),
                                        borderRadius: s(12),
                                        padding: `0 ${spx(16)}`,
                                        border: "1.5px solid #000",
                                        fontSize: spx(BASE.INPUT_FZ),
                                        fontWeight: 900,
                                        boxSizing: "border-box",
                                    }}
                                />

                                {renderHint(idHint)}

                                <div style={{ height: spx(BASE.GAP_INPUT_TO_BTN) }} />
                                <div style={{ display: "grid", placeItems: "center" }}>
                                    <button
                                        type="button"
                                        onClick={findUsername}
                                        disabled={busy}
                                        style={{
                                            width: spx(BASE.BTN_W),
                                            height: spx(BASE.BTN_H),
                                            borderRadius: s(12),
                                            background: "#000",
                                            color: "#fff",
                                            fontSize: spx(BASE.BTN_FZ),
                                            fontWeight: 900,
                                            border: "none",
                                            opacity: busy ? 0.75 : 1,
                                            cursor: busy ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        {busy ? "처리 중…" : "아이디 찾기"}
                                    </button>
                                </div>
                            </>
                        )
                    ) : (
                        <>
                            <Label>아이디</Label>
                            <input
                                placeholder="아이디 입력"
                                inputMode="text"
                                autoComplete="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                style={{
                                    width: spx(BASE.INPUT_W),
                                    height: spx(BASE.INPUT_H),
                                    borderRadius: s(12),
                                    padding: `0 ${spx(16)}`,
                                    border: "1.5px solid #000",
                                    fontSize: spx(BASE.INPUT_FZ),
                                    fontWeight: 900,
                                    boxSizing: "border-box",
                                }}
                            />

                            {renderHint(pwHint)}

                            <div style={{ height: spx(BASE.GAP_INPUT_TO_BTN) }} />
                            <div style={{ display: "grid", placeItems: "center" }}>
                                <button
                                    type="button"
                                    onClick={startPasswordVerification}
                                    disabled={busy}
                                    style={{
                                        width: spx(BASE.BTN_W),
                                        height: spx(BASE.BTN_H),
                                        borderRadius: s(12),
                                        background: "#000",
                                        color: "#fff",
                                        fontSize: spx(BASE.BTN_FZ),
                                        fontWeight: 900,
                                        border: "none",
                                        opacity: busy ? 0.75 : 1,
                                        cursor: busy ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {busy ? "처리 중…" : "인증하기"}
                                </button>
                            </div>

                            {pwSessionIdRef.current && !confirmedRef.current && (
                                <>
                                    <div style={{ height: spx(18) }} />
                                    <div style={{ display: "grid", placeItems: "center" }}>
                                        <button
                                            type="button"
                                            onClick={confirmPasswordVerification}
                                            disabled={busy || confirmInFlightRef.current}
                                            style={{
                                                background: "transparent",
                                                border: "none",
                                                textDecoration: "underline",
                                                fontSize: spx(20),
                                                fontWeight: 900,
                                                color: "#111",
                                                padding: 0,
                                                cursor: "pointer",
                                            }}
                                        >
                                            인증 완료 확인
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    <div style={{ height: spx(40) }} />
                </div>
            </div>
        </div>
    );
}
